import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from 'src/services/user.service';
import { RouteHelperService } from 'src/services/route-helper.service';
import { AuthService } from 'src/services/auth.service';
import firebase from 'firebase/app';

@Component({
  selector: 'app-myparking-access',
  templateUrl: './myparking-access.component.html',
  styleUrls: ['./myparking-access.component.css']
})
export class MyparkingAccessComponent implements OnInit, OnDestroy {

  users: any[] = [];
  filteredUsers: any[] = [];
  searchQuery: string = '';
  isLoading: boolean = true;

  // Make Math available to template
  Math = Math;

  // Pagination
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalUsers: number = 0;

  constructor(
    private userService: UserService,
    private routeHelper: RouteHelperService,
    private router: Router,
    private auth: AuthService
  ) { }

  ngOnInit(): void {
    // Restore admin auth state if returning from user dashboard access
    this.restoreAdminAuthState();
    this.loadUsers();

    // Add window focus event listener to restore auth state when admin returns
    this.addWindowFocusListener();
  }

  ngOnDestroy(): void {
    this.removeWindowFocusListener();
  }

  private windowFocusHandler = () => {
    this.restoreAdminAuthState();
  };

  private addWindowFocusListener(): void {
    window.addEventListener('focus', this.windowFocusHandler);
    window.addEventListener('visibilitychange', this.windowFocusHandler);
  }

  private removeWindowFocusListener(): void {
    window.removeEventListener('focus', this.windowFocusHandler);
    window.removeEventListener('visibilitychange', this.windowFocusHandler);
  }

  async loadUsers(): Promise<void> {
    try {
      this.isLoading = true;
      console.log('Starting to load users...');

      // Use direct Firestore query for better reliability
      await this.loadUsersDirectly();

    } catch (error) {
      console.error('Error loading users:', error);
      this.isLoading = false;
    }
  }

  private async processUsers(users: any[]): Promise<void> {
    console.log("Processing users:", users);
    this.users = users || [];

    // Set default vehicle info without loading from API
    for (let user of this.users) {
      user.vehicles = [];
      user.vehicleCount = 0;
      user.hasVehicles = false;
    }

    this.totalUsers = this.users.length;
    this.applyFilters();
    this.isLoading = false;
    console.log("Finished processing users. Total:", this.totalUsers);
  }

  private async loadUsersDirectly(): Promise<void> {
    try {
      const usersSnapshot = await firebase.firestore().collection('users').limit(50).get();
      const users: any[] = [];

      usersSnapshot.forEach(doc => {
        const data = doc.data();
        users.push({
          id: doc.id,
          uid: doc.id,
          ...data
        });
      });

      console.log("Direct Firestore query result:", users);
      await this.processUsers(users);
    } catch (error) {
      console.error('Error in direct Firestore query:', error);
      this.isLoading = false;
    }
  }


  onSearchChange(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.users];

    // Apply search filter
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(user =>
        (user.displayName && user.displayName.toLowerCase().includes(query)) ||
        (user.phoneNumber && user.phoneNumber.includes(query)) ||
        (user.email && user.email.toLowerCase().includes(query))
      );
    }

    this.filteredUsers = filtered;
  }

  getPaginatedUsers(): any[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredUsers.slice(startIndex, endIndex);
  }

  getTotalPages(): number {
    return Math.ceil(this.filteredUsers.length / this.itemsPerPage);
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.getTotalPages()) {
      this.currentPage = page;
    }
  }

  accessUserDashboard(user: any): void {
    if (!user.id) {
      console.error('User ID is required to access dashboard');
      return;
    }

    // Backup admin's current authentication state before creating session
    this.backupAdminAuthState();

    // Create admin session token for this user access
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const adminSessionData = {
      adminId: currentUser.uid || 'admin',
      targetUserId: user.id,
      timestamp: new Date().toISOString(),
      accessType: 'admin_myparking_access'
    };

    // Store admin session data in sessionStorage
    sessionStorage.setItem('admin_user_access', JSON.stringify(adminSessionData));

    // Navigate to MyParking dashboard with user context
    const dashboardUrl = this.routeHelper.getMyParkingRoute('dashboard');

    // Add query parameters to identify admin access
    const url = `${dashboardUrl}?admin_access=true&user_id=${user.id}`;

    // Open in new tab to keep admin session separate
    window.open(url, '_blank');
  }

  private backupAdminAuthState(): void {
    try {
      // Backup current localStorage user data
      const currentUser = localStorage.getItem('user');
      if (currentUser) {
        localStorage.setItem('admin_backup_user', currentUser);
      }

      // Backup current session data from session storage (if any)
      const sessionKeys = ['parkbot_user_session'];
      sessionKeys.forEach(key => {
        const sessionData = localStorage.getItem(key);
        if (sessionData) {
          localStorage.setItem(`admin_backup_${key}`, sessionData);
        }
      });

      console.log('Admin authentication state backed up');
    } catch (error) {
      console.error('Error backing up admin auth state:', error);
    }
  }

  private restoreAdminAuthState(): void {
    try {
      // Check if there's a backup to restore
      const backupUser = localStorage.getItem('admin_backup_user');

      if (backupUser) {
        // Restore user data
        localStorage.setItem('user', backupUser);
        localStorage.removeItem('admin_backup_user');

        // Restore session data
        const sessionKeys = ['parkbot_user_session'];
        sessionKeys.forEach(key => {
          const backupSession = localStorage.getItem(`admin_backup_${key}`);
          if (backupSession) {
            localStorage.setItem(key, backupSession);
            localStorage.removeItem(`admin_backup_${key}`);
          }
        });

        console.log('Admin authentication state restored');
      }

      // Clean up any admin access session data
      sessionStorage.removeItem('admin_user_access');
    } catch (error) {
      console.error('Error restoring admin auth state:', error);
    }
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';

    if (date.seconds) {
      // Firestore timestamp
      return new Date(date.seconds * 1000).toLocaleDateString();
    }

    return new Date(date).toLocaleDateString();
  }


  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'verified':
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  refresh(): void {
    this.searchQuery = '';
    this.currentPage = 1;
    this.loadUsers();
  }
}